import {
    Component,
    effect,
    EffectRef,
    input,
    InputSignal,
    model,
    ModelSignal,
    OnDestroy,
    OnInit,
    untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Invoice } from '../../invoice.model';
import { CurrencyMaskDirective } from '../../../../UI-Lib/Currency/Mask/currencyMask.directive';
import { ToPercentDirective } from '../../../../UI-Lib/Directives';
import { merge, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { InvoiceDetailOverrides, InvoiceDetailsService, OverrideStatus, SEE_DETAILS } from '../invoiceDetails.service';
import { Decimal } from 'decimal.js';

@Component({
    selector: 'tax-savings-summary',
    templateUrl: './taxSavingsSummary.component.html',
    styleUrls: ['./taxSavingsSummary.component.scss'],
    imports: [CommonModule, FormsModule, ReactiveFormsModule, CurrencyMaskDirective, ToPercentDirective],
})
export class TaxSavingsSummaryComponent implements OnInit, OnDestroy {
    constructor(private readonly _invoiceDetailsService: InvoiceDetailsService) {}

    currentInvoice: ModelSignal<Invoice> = model();
    editMode: InputSignal<boolean> = input();
    invoiceForm: InputSignal<FormGroup> = input();

    hasMultipleTaxableValues: boolean;
    hasAnyTaxableAssessedValue: boolean;
    hasMultipleBaseRates: boolean;
    overrides: InvoiceDetailOverrides;
    overrideStatus = OverrideStatus;
    seeDetailsText = SEE_DETAILS;

    private _destroy$: Subject<void> = new Subject();
    private readonly CALCULATED_BASE_TAV = ['previousTAV', 'invoiceTAV', 'siteChangeTAV'];
    private readonly CALCULATED_BASE_MV = ['previousMV', 'invoiceMV', 'siteChangeMV'];
    private readonly CALCULATED_FIELDS = {
        siteChangeTAV: ['previousTAV', 'invoiceTAV'],
        siteChangeMV: ['previousMV', 'invoiceMV'],
        billableTaxRate: ['previousTAV', 'invoiceTAV', 'siteChangeTAV', 'taxSavingsTotal'],
        taxSavingsTotal: ['previousTAV', 'invoiceTAV', 'siteChangeTAV', 'billableTaxRate'],
        feeAmount: [
            'previousTAV',
            'invoiceTAV',
            'siteChangeTAV',
            'billableTaxRate',
            'contingencyPct',
            'contingencyCap',
            'taxSavingsTotal',
        ],
        invoiceAmount: [
            'previousTAV',
            'invoiceTAV',
            'siteChangeTAV',
            'billableTaxRate',
            'contingencyPct',
            'contingencyCap',
            'taxSavingsTotal',
            'feeAmount',
            'fixedFee',
        ],
    };

    private readonly _invoiceEffect: EffectRef = effect(() => {
        const invoice = this.currentInvoice();
        if (!invoice) return;

        untracked(() => {
            if (!this.invoiceForm() || this._invoiceDetailsService.invoiceInitialized) return;

            this._processInvoiceChanges(invoice);
        });
    });

    private readonly _editModeEffect: EffectRef = effect(() => {
        const editMode = this.editMode();
        if (editMode === undefined) {
            return;
        }

        untracked(() => {
            if (!this.invoiceForm()) {
                return;
            }
            if (!editMode) {
                this.invoiceForm()
                    .get('includeOtherParcels')
                    .patchValue(this._invoiceDetailsService.invoiceResetValues.includeOtherParcels);
                this.invoiceForm().patchValue(this._invoiceDetailsService.invoiceResetValues, { emitEvent: false });
                this._processOverrides();
                this.invoiceForm().disable({ emitEvent: false });
            } else {
                this.invoiceForm().enable({ emitEvent: false });

                if (!this.hasAnyTaxableAssessedValue) {
                    this.invoiceForm().get('previousTAV').disable({ emitEvent: false });
                    this.invoiceForm().get('invoiceTAV').disable({ emitEvent: false });
                }
                // Force calculated controls to remain disabled in the UI
                this.invoiceForm().get('siteChangeTAV')?.disable({ emitEvent: false });
                this.invoiceForm().get('siteChangeMV')?.disable({ emitEvent: false });
            }
        });
    });

    ngOnInit(): void {
        this.overrides = this._invoiceDetailsService.overrides;

        const previousTAV = this.invoiceForm().get('previousTAV');
        const invoiceTAV = this.invoiceForm().get('invoiceTAV');
        const siteChangeTAV = this.invoiceForm().get('siteChangeTAV');
        const previousMV = this.invoiceForm().get('previousMV');
        const invoiceMV = this.invoiceForm().get('invoiceMV');
        const siteChangeMV = this.invoiceForm().get('siteChangeMV');
        const taxSavingsTotal = this.invoiceForm().get('taxSavingsTotal');
        const billableTaxRate = this.invoiceForm().get('billableTaxRate');
        const contingencyPct = this.invoiceForm().get('contingencyPct');
        const contingencyCap = this.invoiceForm().get('contingencyCap');
        const feeAmount = this.invoiceForm().get('feeAmount');
        const fixedFee = this.invoiceForm().get('fixedFee');
        const expenseAmount = this.invoiceForm().get('expenseAmount');

        // Compute site TAV change
        merge(previousTAV.valueChanges, invoiceTAV.valueChanges)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                const prevTAV = previousTAV.value || this._invoiceDetailsService.originalInvoiceValues.previousTAV;
                const invTAV = invoiceTAV.value || this._invoiceDetailsService.originalInvoiceValues.invoiceTAV;

                if (prevTAV === null && invTAV === null) {
                    return;
                }

                siteChangeTAV.patchValue(Number(prevTAV) - Number(invTAV));
            });

        // Compute site MV change
        merge(previousMV.valueChanges, invoiceMV.valueChanges)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                siteChangeMV.patchValue(Number(previousMV.value) - Number(invoiceMV.value));
            });

        // Handle updating billable or tax savings
        siteChangeTAV.valueChanges.pipe(takeUntil(this._destroy$), distinctUntilChanged()).subscribe(() => {
            if (!this.hasAnyTaxableAssessedValue) {
                return;
            }
            if (this.overrides.taxSavingsTotal === OverrideStatus.Manual || taxSavingsTotal.dirty) {
                this._updatebillableTaxRate(true);
            } else {
                this._updateTaxSavings(false);
            }
        });

        // Handle updating billable or tax savings
        siteChangeMV.valueChanges.pipe(takeUntil(this._destroy$), distinctUntilChanged()).subscribe(() => {
            if (this.hasAnyTaxableAssessedValue) {
                return;
            }
            if (this.overrides.taxSavingsTotal === OverrideStatus.Manual || taxSavingsTotal.dirty) {
                this._updatebillableTaxRate(true);
            } else {
                this._updateTaxSavings(false);
            }
        });

        // Compute billable rate
        taxSavingsTotal.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
            if (taxSavingsTotal.dirty) {
                billableTaxRate.markAsPristine({ emitEvent: false });
            }
            this._updatebillableTaxRate(false);
        });

        // Compute tax savings
        billableTaxRate.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
            if (billableTaxRate.dirty) {
                taxSavingsTotal.markAsPristine({ emitEvent: false });
            }
            this._updateTaxSavings(false);
        });

        // Compute fee amount
        merge(contingencyCap.valueChanges, contingencyPct.valueChanges, taxSavingsTotal.valueChanges)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                if (this._isOverridden('feeAmount') !== OverrideStatus.Manual) {
                    feeAmount.patchValue(this._calculateFeeAmount(), {
                        emitEvent: false,
                    });
                }
                this._updateInvoiceAmount();
                this._processOverrides();
            });

        // Calculate invoice amount
        merge(
            taxSavingsTotal.valueChanges,
            feeAmount.valueChanges,
            fixedFee.valueChanges,
            expenseAmount.valueChanges,
        ).subscribe(() => this._updateInvoiceAmount());

        feeAmount.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this._updateFeeAmount());

        // Update invoice and monitor overrides
        this.invoiceForm()
            .valueChanges.pipe(
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                takeUntil(this._destroy$),
            )
            .subscribe(() => this._processOverrides());
    }

    ngOnDestroy(): void {
        this._invoiceEffect?.destroy();
        this._editModeEffect?.destroy();
        this._destroy$.next();
        this._destroy$.complete();
    }

    /**
     * Determines whether a field should display "See Details..." in the template.
     * This replaces the old approach of storing SEE_DETAILS string in form controls.
     */
    showSeeDetails(formControlName: string): boolean {
        const seeDetailsFields = ['previousTAV', 'invoiceTAV', 'siteChangeTAV', 'billableTaxRate'];
        if (!seeDetailsFields.includes(formControlName)) {
            return false;
        }

        // For siteChangeTAV: show "See Details" unless both previousTAV and invoiceTAV are manually overridden
        if (formControlName === 'siteChangeTAV') {
            return !(
                this.overrides.previousTAV === OverrideStatus.Manual &&
                this.overrides.invoiceTAV === OverrideStatus.Manual
            );
        }

        // For billableTaxRate: show "See Details" unless taxSavingsTotal or billableTaxRate is manually overridden
        if (formControlName === 'billableTaxRate') {
            return (
                this.overrides.taxSavingsTotal !== OverrideStatus.Manual &&
                this.overrides.billableTaxRate !== OverrideStatus.Manual
            );
        }

        // For previousTAV and invoiceTAV: show "See Details" unless the field itself is manually overridden
        return this.overrides[formControlName] !== OverrideStatus.Manual;
    }

    resetFeeAmount(): void {
        this.overrides.feeAmount = OverrideStatus.None;
        const feeAmount = this.invoiceForm().get('feeAmount');
        feeAmount.patchValue(this._calculateFeeAmount());
        feeAmount.markAsPristine({ emitEvent: false });
        this.overrides.feeAmount = this._isOverridden('feeAmount');
    }

    resetOverride(formControlNames: string[], emitEvent: boolean = true): void {
        formControlNames.forEach((formControlName) => {
            this.overrides[formControlName] = OverrideStatus.None;
            const formControl = this.invoiceForm().get(formControlName);
            const resetValue = this._invoiceDetailsService.originalInvoiceValues[formControlName];
            if (!emitEvent) {
                // If emitEvent is false, it means we're not triggering any of the related calculations.
                // In this case, we need to directly set the value on the current invoice for saving.
                this.currentInvoice.set({
                    ...this.currentInvoice(),
                    [formControlName]: this._invoiceDetailsService.originalInvoiceValues[formControlName],
                });
            }
            formControl.patchValue(resetValue, { emitEvent: true });
            formControl.markAsPristine({ emitEvent: false });
        });

        this._processOverrides();
    }

    private _isOverridden(formControlName: string): OverrideStatus {
        const control = this.invoiceForm().get(formControlName);
        const formValue = Object.keys(this.invoiceForm().getRawValue()).includes(formControlName)
            ? control.value
            : this.currentInvoice()[formControlName];

        const changed =
            Number(formValue) !== Number(this._invoiceDetailsService.originalInvoiceValues[formControlName]);

        if (changed && this.CALCULATED_FIELDS[formControlName]) {
            return !control?.dirty &&
                this.CALCULATED_FIELDS[formControlName].some((x) => this.overrides[x] === OverrideStatus.Manual)
                ? OverrideStatus.Auto
                : OverrideStatus.Manual;
        }

        if (changed) {
            return OverrideStatus.Manual;
        } else {
            return OverrideStatus.None;
        }
    }

    private _updatebillableTaxRate(emitEvent: boolean): void {
        const changeValue = this.hasAnyTaxableAssessedValue ? 'siteChangeTAV' : 'siteChangeMV';
        const siteChangeControl = this.invoiceForm().get(changeValue);
        const billableTaxRate = this.invoiceForm().get('billableTaxRate');
        const taxSavingsTotal = this.invoiceForm().get('taxSavingsTotal');
        const feeAmount = this.invoiceForm().get('feeAmount');

        if (taxSavingsTotal.value === null) {
            return;
        }

        if (
            this.hasMultipleTaxableValues &&
            (this._isOverridden('taxSavingsTotal') === OverrideStatus.None ||
                this._isOverridden('siteChangeTAV') === OverrideStatus.None)
        ) {
            // Template will show "See Details..." based on override status; skip calculation
            return;
        }

        // If the user has chosen Billable Rate as manual, don't overwrite it.
        if (billableTaxRate.dirty) {
            return;
        }

        // In this path, Tax Savings is treated as manual source.
        billableTaxRate.markAsPristine({ emitEvent: false });
        this.currentInvoice.set({
            ...this.currentInvoice(),
            isSavingsOverridden: true,
        });

        const siteChange =
            Number(siteChangeControl.value) ||
            Number(this._invoiceDetailsService.originalInvoiceValues[changeValue]) ||
            1;
        const savings =
            Number(taxSavingsTotal.value) || Number(this._invoiceDetailsService.originalInvoiceValues.taxSavingsTotal);

        if (siteChange === 0) {
            // Avoid divide-by-zero; keep the current value.
            billableTaxRate.patchValue(0, { emitEvent });
            return;
        }

        const value = savings / siteChange;
        billableTaxRate.patchValue(Number(value.toFixed(8)), { emitEvent });

        feeAmount.patchValue(this._calculateFeeAmount(), {
            emitEvent: false,
        });
        this.overrides.feeAmount = this._isOverridden('feeAmount');
        this._updateInvoiceAmount();
    }

    private _updateTaxSavings(emitEvent: boolean): void {
        const changeValue = this.hasAnyTaxableAssessedValue ? 'siteChangeTAV' : 'siteChangeMV';
        const siteChangeControl = this.invoiceForm().get(changeValue);
        const billableTaxRate = this.invoiceForm().get('billableTaxRate');
        const taxSavingsTotal = this.invoiceForm().get('taxSavingsTotal');
        const feeAmount = this.invoiceForm().get('feeAmount');

        if (billableTaxRate.value === null) {
            return;
        }

        const isBillableTaxRateOverridden = this._isOverridden('billableTaxRate') !== OverrideStatus.None;
        const isSiteChangeOverridden = this._isOverridden(changeValue) !== OverrideStatus.None;

        if (
            (this.hasMultipleBaseRates &&
                this._isOverridden('billableTaxRate') === OverrideStatus.None &&
                this._isOverridden('siteChangeTAV') === OverrideStatus.None) ||
            (this.hasMultipleTaxableValues &&
                (this._isOverridden('billableTaxRate') === OverrideStatus.None ||
                    this._isOverridden('siteChangeTAV') === OverrideStatus.None)) ||
            (!isBillableTaxRateOverridden && !isSiteChangeOverridden)
        ) {
            taxSavingsTotal.patchValue(this.currentInvoice().taxSavingsTotalOriginal, {
                emitEvent,
            });
            return;
        }

        // If the user has chosen Tax Savings as manual, don't overwrite it.
        if (taxSavingsTotal.dirty) {
            return;
        }

        // In this path, Billable Rate is treated as manual source.
        taxSavingsTotal.markAsPristine({ emitEvent: false });
        this.currentInvoice.set({
            ...this.currentInvoice(),
            isSavingsOverridden: false,
        });

        const siteChange =
            Number(siteChangeControl.value) || Number(this._invoiceDetailsService.originalInvoiceValues[changeValue]);
        const rate =
            Number(billableTaxRate.value) || Number(this._invoiceDetailsService.originalInvoiceValues.billableTaxRate);

        const updatedValue = siteChange * rate;
        taxSavingsTotal.patchValue(Number(updatedValue.toFixed(2)), { emitEvent });

        feeAmount.patchValue(this._calculateFeeAmount(), {
            emitEvent: false,
        });
        this.overrides.feeAmount = this._isOverridden('feeAmount');
        this._updateInvoiceAmount();
    }

    private _updateFeeAmount(): void {
        const feeAmount = this.invoiceForm().get('feeAmount');
        if (!feeAmount.value && feeAmount.value !== 0) {
            feeAmount.patchValue(this._calculateFeeAmount(), { emitEvent: false });
        }

        const calculatedFeeAmount = this._calculateFeeAmount();

        if (
            feeAmount.value === calculatedFeeAmount ||
            feeAmount.value === this._invoiceDetailsService.originalInvoiceValues['feeAmount']
        ) {
            this.overrides.feeAmount = this._isOverridden('feeAmount');
        } else {
            this.overrides.feeAmount = OverrideStatus.Manual;
        }
    }

    private _updateInvoiceAmount(): void {
        const feeAmount = this.invoiceForm().get('feeAmount');
        const fixedFee = this.invoiceForm().get('fixedFee');
        const expenseAmount = this.invoiceForm().get('expenseAmount');

        const totalFeeAmount = (Number(feeAmount.value) || 0) + (Number(fixedFee.value) || 0);
        const invoiceAmount = totalFeeAmount + Number(expenseAmount.value);

        this.currentInvoice.set({
            ...this.currentInvoice(),
            totalFeeAmount: totalFeeAmount,
            invoiceAmount: invoiceAmount,
        });
        this.overrides.invoiceAmount = this._isOverridden('invoiceAmount');
    }

    private _calculateFeeAmount(): number {
        if (this.overrides.feeAmount === OverrideStatus.Manual) {
            return this.invoiceForm().get('feeAmount').value;
        }

        const taxSavings = this.invoiceForm().get('taxSavingsTotal').value;

        const contingencyPct = this.invoiceForm().get('contingencyPct').value;
        if (!contingencyPct) {
            return 0;
        }

        const totalSavingsDec: Decimal = new Decimal(taxSavings);
        let calcFee: number = totalSavingsDec.times(contingencyPct).toNumber();

        const contingencyCap = this.invoiceForm().get('contingencyCap').value;
        if (contingencyCap !== undefined && contingencyCap !== null && calcFee > contingencyCap) {
            calcFee = contingencyCap;
        }

        return new Decimal(calcFee).toDecimalPlaces(2).toNumber();
    }

    private _processOverrides(): void {
        Object.keys(this.overrides).forEach((key) => {
            this.overrides[key] = OverrideStatus.None;
        });

        const keys: string[] = [
            'previousTAV',
            'previousMV',
            'invoiceTAV',
            'invoiceMV',
            'siteChangeTAV',
            'siteChangeMV',
            'contingencyPct',
            'contingencyCap',
            'fixedFee',
        ];

        if (this.currentInvoice().isSavingsOverridden) {
            keys.push('taxSavingsTotal', 'billableTaxRate', 'feeAmount', 'invoiceAmount');
        } else {
            keys.push('billableTaxRate', 'taxSavingsTotal', 'feeAmount', 'invoiceAmount');
        }

        keys.forEach((key: string) => {
            this.overrides[key] = this._isOverridden(key);
        });

        // Handle tax savings/billable rate special case
        if (this.overrides.taxSavingsTotal && this.currentInvoice().isSavingsOverridden) {
            this.overrides.taxSavingsTotal = OverrideStatus.Manual;
            this.overrides.billableTaxRate = this._isOverridden('billableTaxRate');
        } else if (this.overrides.billableTaxRate) {
            this.overrides.billableTaxRate = OverrideStatus.Manual;
            this.overrides.taxSavingsTotal = this._isOverridden('taxSavingsTotal');
        }

        // Handle fee amount special case
        this._updateFeeAmount();

        // If they are manual overrides mark as dirty
        if (this.invoiceForm()) {
            keys.forEach((key: string) => {
                const formControl = this.invoiceForm().get(key);
                if (formControl && this.overrides[key] === OverrideStatus.Manual) {
                    formControl.markAsDirty({ onlySelf: true });
                }
            });
        }
    }

    private _processInvoiceChanges(invoice: Invoice): void {
        this.hasMultipleTaxableValues = invoice.taxSavings.some((x) =>
            x.parcelSavingsCalculations.some(
                (y) => y.appealRevisionCalculations.length > 1 || y.outcomeRevisionCalculations.length > 1,
            ),
        );

        const formattedTotals = this._invoiceDetailsService.processInvoiceChanges(invoice);

        this.invoiceForm().markAsPristine({ emitEvent: false });
        this.invoiceForm().patchValue(
            { ...formattedTotals, includeOtherParcels: invoice.includeOtherParcels },
            { emitEvent: false },
        );

        this._updateInvoiceAmount();

        this._processOverrides();

        Object.keys(formattedTotals).forEach((x) => {
            if (formattedTotals[x] !== undefined) {
                invoice[x] = formattedTotals[x];
            }
        });

        this.currentInvoice.set(invoice);
        this._invoiceDetailsService.generateNotes(this.currentInvoice());

        this.hasAnyTaxableAssessedValue = this._invoiceDetailsService.hasAnyTaxableAssessedValue;
        this.hasMultipleBaseRates = this._invoiceDetailsService.hasMultipleBaseRates;

        const base = this.hasAnyTaxableAssessedValue ? this.CALCULATED_BASE_TAV : this.CALCULATED_BASE_MV;
        const calculatedFields = ['billableTaxRate', 'taxSavingsTotal', 'feeAmount', 'invoiceAmount'];
        calculatedFields.forEach((field) => {
            this.CALCULATED_FIELDS[field].splice(0, base.length, ...base);
        });

        this._invoiceDetailsService.invoiceInitialized = true;
    }
}
